import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type {
  DataChangedEvent,
  LoadingFactsReadyPayload,
  RenderStagesReadyPayload,
} from '../realtime-sync.types';
import type { LoadingFactsAttributes } from '@/features/design/render/api/loading-facts.service';

export async function handleLoadingFactsReady(event: DataChangedEvent): Promise<void> {
  const payload = event.payload as LoadingFactsReadyPayload | undefined;
  if (!payload?.facts || !payload.project_id) return;

  wsLogger.info('[RealtimeSync] Loading facts ready:', {
    projectId: payload.project_id,
    factsCount: payload.facts.length,
  });

  // Populate TanStack Query cache for all locales matching this projectId.
  // MERGE, don't replace: the WS payload carries only facts, but the cached
  // entry from the cold GET also holds `stages` (static design-stage list) and
  // `seen_ids`. A blind overwrite dropped `stages`, so the stage line under the
  // progress bar vanished the instant the first WS push landed at render start.
  queryClient.setQueriesData<LoadingFactsAttributes>(
    { queryKey: cacheKeys.loadingFacts.byProject(payload.project_id) },
    (prev) => ({
      ...prev,
      // WS payload types facts loosely (type: string); the API contract keeps
      // them to the fact/tip/trivia union used by the cache entry.
      facts: payload.facts as LoadingFactsAttributes['facts'],
      is_contextual: payload.is_contextual,
      seen_ids: prev?.seen_ids ?? [],
      stages: prev?.stages ?? [],
    })
  );
}

/**
 * Per-render design stages pushed by GeneratePromptJob once the prompt LLM has
 * tailored the stage list to the actual change (short for a small refine, full
 * for a big redesign). We merge them into the SAME `stages` slot the render
 * progress overlay already reads — replacing the static fallback list from the
 * cold GET — so no overlay change is needed. Facts/seen_ids are preserved.
 */
export async function handleRenderStagesReady(event: DataChangedEvent): Promise<void> {
  const payload = event.payload as RenderStagesReadyPayload | undefined;
  if (!payload?.stages?.length || !payload.project_id) return;

  wsLogger.info('[RealtimeSync] Render stages ready:', {
    projectId: payload.project_id,
    renderId: payload.render_id,
    stagesCount: payload.stages.length,
  });

  queryClient.setQueriesData<LoadingFactsAttributes>(
    { queryKey: cacheKeys.loadingFacts.byProject(payload.project_id) },
    (prev) => ({
      facts: prev?.facts ?? [],
      is_contextual: prev?.is_contextual ?? false,
      seen_ids: prev?.seen_ids ?? [],
      ...prev,
      stages: payload.stages,
    })
  );
}
